/* Single-product purchasing uses Shopify variant IDs, prices and availability. */
(() => {
  class BWItem extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      const data = this.querySelector('[data-bw-item-data]');
      try {
        this.product = JSON.parse(data?.textContent || 'null');
        if (!this.product) return;
        this.form = this.querySelector('form');
        this.native = this.querySelector('[data-bw-item-native]');
        this.groups = [...this.querySelectorAll('[data-bw-item-option]')];
        this.quantity = this.querySelector('[data-bw-item-quantity]');
        this.button = this.querySelector('[data-bw-item-submit]');
        this.error = this.querySelector('[data-bw-item-error]');
        this.money = new Intl.NumberFormat(this.dataset.locale || 'de-DE', { style: 'currency', currency: this.dataset.currency || 'EUR' });
        this.variants = (this.product.variants || []).filter(variant => this.allowed(variant));
        const params = new URL(location.href).searchParams;
        const requested = this.variants.find(v => String(v.id) === params.get('variant')) || this.variants.find(v => v.options[0] === params.get('bw_modules'));
        const initial = requested || this.variants.find(v => String(v.id) === this.native.value) || this.variants.find(v => v.available) || this.variants[0];
        this.select(initial);
        this.form.addEventListener('change', event => {
          if (event.target === this.native) this.select(this.variants.find(v => String(v.id) === this.native.value));
          this.update();
        });
        this.quantity.addEventListener('input', () => this.update(false));
        this.form.addEventListener('submit', event => this.submit(event));
        this.update(false);
        this.dataset.ready = 'true';
      } catch (error) {
        console.warn('Balkonwunder: Einzelprodukt-Auswahl konnte nicht geladen werden.', error);
        // Keep the standard Shopify form and native variant dropdown usable.
      }
    }
    allowed(variant) {
      if (this.dataset.kind !== 'panels') return true;
      if (!['2 Module', '4 Module'].includes(variant.options?.[0])) return false;
      const names = this.product.options_with_values?.map(option => option.name) || this.product.options || [];
      return variant.options.slice(1).every((value, index) => {
        const name = String(names[index + 1] || '').toLowerCase();
        if (name.includes('system')) return value === 'Wechselrichter';
        if (/schuko|schucko|kabel/.test(name)) return ['5 m', '5m', '5 Meter'].includes(value);
        return false;
      });
    }
    select(variant) {
      if (!variant) return;
      this.groups.forEach(group => {
        const index = Number(group.dataset.bwItemOption);
        group.querySelectorAll('input').forEach(input => { input.checked = input.value === variant.options[index]; });
      });
      this.native.value = variant.id;
    }
    update(updateUrl = true) {
      const chosen = this.groups.map(group => [Number(group.dataset.bwItemOption), group.querySelector('input:checked')?.value]);
      this.variant = this.groups.length ? this.variants.find(v => chosen.every(([i, value]) => v.options[i] === value)) : this.variants.find(v => String(v.id) === this.native.value) || this.variants[0];
      const quantity = Number(this.quantity.value);
      this.quantityValid = Number.isInteger(quantity) && quantity >= 1 && quantity <= 99;
      const shipping = this.dataset.kind === 'panels' ? (parseInt(this.variant?.options?.[0], 10) === 4 ? 9300 : 4650) : 907;
      this.querySelector('[data-bw-item-shipping]').textContent = this.variant ? 'Produkt ' + this.money.format(this.variant.price / 100) + ' + Versand ' + this.money.format(shipping / 100) : '';
      this.querySelector('[data-bw-item-price]').textContent = this.variant ? this.money.format((this.variant.price + shipping) / 100) : 'Zurzeit nicht verfügbar';
      this.native.value = this.variant?.id || '';
      this.button.disabled = this.dataset.demo === 'true' || this.busy || !this.variant?.available || !this.quantityValid;
      this.querySelector('[data-bw-item-submit-label]').textContent = this.busy ? 'Wird hinzugefügt …' : this.dataset.demo === 'true' ? 'Zurzeit nicht verfügbar' : !this.variant?.available ? 'Zurzeit nicht verfügbar' : !this.quantityValid ? 'Anzahl prüfen' : 'In den Warenkorb';
      const actual = this.variant?.featured_image?.src || this.variant?.featured_image?.url;
      const moduleWord = ({ 2: 'Two', 3: 'Three', 4: 'Four' })[parseInt(this.variant?.options?.[0], 10)];
      const url = actual || (this.dataset.kind === 'panels' && moduleWord ? this.dataset['image' + moduleWord] : null);
      if (url) {
        const image = this.querySelector('.bw-item-image');
        image.removeAttribute('srcset');
        image.src = url;
        image.alt = this.product.title + ' · ' + this.variant.title;
      }
      if (updateUrl && this.variant && this.dataset.demo !== 'true') {
        const url = new URL(location.href);
        url.searchParams.set('variant', this.variant.id);
        url.searchParams.delete('bw_modules');
        history.replaceState({}, '', url);
      }
    }
    async submit(event) {
      event.preventDefault();
      if (this.dataset.demo === 'true' || this.busy || !this.variant?.available || !this.quantityValid || !this.form.reportValidity()) return;
      this.busy = true;
      this.error.textContent = '';
      this.button.setAttribute('aria-busy', 'true');
      this.update(false);
      try {
        const root = this.dataset.root || window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: this.variant.id, quantity: Number(this.quantity.value) }] })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'Das Produkt konnte nicht hinzugefügt werden.');
        location.assign(this.dataset.cartUrl || root + 'cart');
      } catch (error) {
        this.error.textContent = (error.message || 'Die Anfrage konnte nicht abgeschlossen werden.') + ' Bitte prüfe den Warenkorb vor einem erneuten Versuch.';
        const link = document.createElement('a');
        link.href = this.dataset.cartUrl || '/cart';
        link.textContent = ' Warenkorb öffnen';
        this.error.append(link);
        this.error.focus();
        this.busy = false;
        this.button.removeAttribute('aria-busy');
        this.update(false);
      }
    }
  }
  if (!customElements.get('bw-item')) customElements.define('bw-item', BWItem);
})();
